import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const DISMISS_KEY = 'memara.dctw26.banner.dismissed';

interface DctwOfferProps {
  variant: 'banner' | 'section';
}

export default function DctwOffer({ variant }: DctwOfferProps) {
  const { t } = useTranslation('homepage');
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    if (variant !== 'banner') return;
    try {
      setDismissed(window.localStorage.getItem(DISMISS_KEY) === '1');
    } catch {
      setDismissed(false);
    }
  }, [variant]);

  const dismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore storage failures */
    }
  };

  if (variant === 'banner') {
    if (dismissed) return null;
    return (
      <div className="border-b bg-indigo-700 text-white">
        <div className="container mx-auto flex items-start gap-3 px-4 py-3 text-sm md:items-center">
          <p className="flex-1">
            {t('dctw.banner')}{' '}
            <a href="#dctw" className="font-semibold underline underline-offset-2">
              {t('dctw.howToRedeem')}
            </a>
          </p>
          <button
            type="button"
            onClick={dismiss}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md hover:bg-indigo-600"
            aria-label={t('dctw.dismiss')}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  const steps = [
    t('dctw.steps.1'),
    t('dctw.steps.2'),
    t('dctw.steps.3'),
    t('dctw.steps.4'),
  ];

  return (
    <section id="dctw" className="mx-auto max-w-3xl scroll-mt-24 rounded-xl border bg-white p-6 text-left shadow-sm md:p-8">
      <h2 className="text-2xl font-bold text-foreground">{t('dctw.title')}</h2>
      <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm text-muted-foreground md:text-base">
        {steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      <p className="mt-4 text-sm text-foreground">{t('dctw.monthlyOnly')}</p>
      <p className="mt-2 text-sm text-muted-foreground">{t('dctw.firstTime')}</p>
      <p className="mt-2 text-sm text-muted-foreground">{t('dctw.cardRequired')}</p>
      <p className="mt-4 text-sm">
        <Link to="/signup" className="font-medium text-indigo-700 hover:underline">
          {t('dctw.startFree')}
        </Link>
      </p>
    </section>
  );
}
